import { API_BASE_URL } from '../config/api.js';

// --- Helper: Enhanced fetch with error handling ---
async function enhancedFetch(url, options = {}, meta = {}) {
  const method = options.method || 'GET';
  const endpoint = meta.endpoint || url;

  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  const finalOptions = { ...options, headers };

  const start = Date.now();
  try {
    const response = await fetch(url, finalOptions);
    const duration = Date.now() - start;

    console.log(`API ${method} ${endpoint}:`, {
      status: response.status,
      duration: `${duration}ms`,
      url
    });

    if (!response.ok) {
      console.error(`API error: ${method} ${endpoint}`, {
        status: response.status,
        url,
        duration
      });
    }

    return response;
  } catch (error) {
    const duration = Date.now() - start;
    console.error(`API network error: ${method} ${endpoint}`, {
      error: error.message,
      url,
      duration
    });
    throw error;
  }
}

// --- 1. Public Fetch (Unauthenticated) ---
// Used for fetching products, guest carts, and other open endpoints.
export const publicFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  return enhancedFetch(url, options, { endpoint });
};

// --- 2. Authenticated Fetch (Web Version) ---
// Handles authentication for logged-in users (Firebase Auth or stored session)
export const authFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  try {
    let headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    };

    let authAttempted = false;

    // Try Firebase auth first (primary authentication method)
    if (typeof window !== 'undefined') {
      try {
        const { auth } = await import('../config/firebase.js');
        const currentUser = auth.currentUser;
        
        if (currentUser) {
          try {
            const idToken = await currentUser.getIdToken(false); // Don't force refresh
            headers.Authorization = `Bearer ${idToken}`;
            authAttempted = true;
            
            console.log(`Making authenticated request (Firebase) to: ${url}`);
            
            const response = await enhancedFetch(
              url,
              {
                ...options,
                headers,
              },
              { endpoint }
            );
            
            // If Firebase auth succeeds, return immediately
            if (response.ok || response.status !== 401) {
              return response;
            }
            
            console.log('Firebase auth failed with 401, trying fallback...');
          } catch (tokenError) {
            console.log('Firebase token error, trying fallback auth:', tokenError);
          }
        }
      } catch (firebaseError) {
        console.log('Firebase auth not available, trying stored credentials:', firebaseError);
      }
    }
    
    // Fallback to stored session (for backward compatibility)
    const storedCustomerId = localStorage.getItem('customerId');
    const storedEmail = localStorage.getItem('userEmail');

    if (!storedCustomerId || !storedEmail) {
      if (authAttempted) {
        throw new Error('Firebase authentication failed and no stored session available');
      } else {
        throw new Error('Authentication required for this operation.');
      }
    }

    headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      'X-Customer-ID': storedCustomerId,
      'X-Customer-Email': storedEmail,
    };
    // Remove Firebase auth header for session fallback
    delete headers.Authorization;

    console.log(
      `Making authenticated request (Session fallback) to: ${url} with Customer ID: ${storedCustomerId}`
    );

    return await enhancedFetch(
      url,
      {
        ...options,
        headers,
      },
      { endpoint }
    );
  } catch (error) {
    console.error('Error in authFetch:', error);
    throw error;
  }
};

// Generic API call function with error handling
const apiCall = async (url, options = {}) => {
  try {
    const response = await enhancedFetch(url, options);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

// Products API
export const productsApi = {
  // Get trending products for home page
  getTrendingProducts: async (page = 0, size = 10) => {
    const url = `${API_BASE_URL}/api/products/trending?page=${page}&size=${size}`;
    return await apiCall(url);
  },

  // Get new arrivals for home page
  getNewArrivals: async (page = 0, size = 12, days = 30) => {
    const url = `${API_BASE_URL}/api/products/new-arrivals?page=${page}&size=${size}&days=${days}`;
    return await apiCall(url);
  },

  // Get home page stats
  getHomeStats: async () => {
    const url = `${API_BASE_URL}/api/home/stats`;
    return await apiCall(url);
  },

  // Get all products with filters
  getAllProducts: async (params = {}) => {
    const urlParams = new URLSearchParams({
      page: params.page || 0,
      size: params.size || 20,
      ...(params.category && { category: params.category }),
      ...(params.subCategory && { subCategory: params.subCategory }),
      ...(params.sort && { sort: params.sort }),
      ...(params.search && { search: params.search }),
      ...(params.minPrice && { minPrice: params.minPrice }),
      ...(params.maxPrice && { maxPrice: params.maxPrice }),
    });
    
    const url = `${API_BASE_URL}/api/products/mobile?${urlParams.toString()}`;
    return await apiCall(url);
  },

  // Get product details by ID
  getProductById: async (productId) => {
    const url = `${API_BASE_URL}/api/products/${productId}`;
    console.log('%c[API Service] getProductById called with:', 'color: orange; font-weight: bold;', productId);
    console.log('%c[API Service] Full URL:', 'color: orange;', url);
    console.log('%c[API Service] API_BASE_URL:', 'color: orange;', API_BASE_URL);
    
    // Test if productId is valid
    if (!productId || productId === 'undefined' || productId === 'null') {
      console.error('%c[API Service] Invalid productId provided:', 'color: red;', productId);
      throw new Error(`Invalid product ID: ${productId}`);
    }
    
    try {
      const result = await apiCall(url);
      console.log('%c[API Service] getProductById SUCCESS:', 'color: green;', result);
      return result;
    } catch (error) {
      console.error('%c[API Service] getProductById ERROR:', 'color: red;', error);
      console.error('%c[API Service] Error details:', 'color: red;', {
        url,
        productId,
        errorMessage: error.message,
        errorStatus: error.status
      });
      throw error;
    }
  },

};

// Categories API (for future use)
export const categoriesApi = {
  getAllCategories: async () => {
    const url = `${API_BASE_URL}/api/categories`;
    return await apiCall(url);
  },
};

// Orders API
export const ordersApi = {
  // Get orders by customer ID (secure method)
  getOrdersByCustomerId: async (customerId) => {
    const url = `${API_BASE_URL}/api/orders/customer/${customerId}`;
    return await apiCall(url);
  },

  // Get order status history
  getOrderStatusHistory: async (orderId) => {
    const url = `${API_BASE_URL}/api/orders/${orderId}/status-history`;
    return await apiCall(url);
  },

  // Download order invoice
  downloadInvoice: async (orderId) => {
    const response = await authFetch(`/api/orders/${orderId}/invoice`, {
      method: 'GET',
      headers: {
        'Accept': 'application/pdf',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download invoice');
    }

    return response.blob();
  },
};

// Wishlist API
export const wishlistApi = {
  // Get all wishlist items for current user
  getWishlist: async () => {
    const response = await authFetch('/api/wishlist');
    if (!response.ok) {
      throw new Error('Failed to fetch wishlist');
    }
    return response.json();
  },

  // Add product to wishlist
  addToWishlist: async (productId) => {
    const response = await authFetch(`/api/wishlist/product/${productId}`, {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error('Failed to add to wishlist');
    }
    return response.json();
  },

  // Remove product from wishlist
  removeFromWishlist: async (productId) => {
    const response = await authFetch(`/api/wishlist/remove/${productId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to remove from wishlist');
    }
  },

  // Clear entire wishlist
  clearWishlist: async () => {
    const response = await authFetch('/api/wishlist/clear', {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to clear wishlist');
    }
  },
};

// Addresses API
export const addressesApi = {
  // Get all addresses for current user
  getAllAddresses: async () => {
    const url = `${API_BASE_URL}/api/addresses`;
    return await apiCall(url);
  },

  // Get address by ID
  getAddressById: async (id) => {
    const url = `${API_BASE_URL}/api/addresses/${id}`;
    return await apiCall(url);
  },

  // Create new address
  createAddress: async (addressData) => {
    const response = await authFetch('/api/addresses', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(addressData),
    });

    if (!response.ok) {
      throw new Error('Failed to create address');
    }

    return response.json();
  },

  // Update address
  updateAddress: async (id, addressData) => {
    const response = await authFetch(`/api/addresses/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(addressData),
    });

    if (!response.ok) {
      throw new Error('Failed to update address');
    }

    return response.json();
  },

  // Delete address
  deleteAddress: async (id) => {
    const response = await authFetch(`/api/addresses/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to delete address');
    }
  },

  // Set default address
  setDefaultAddress: async (id) => {
    const response = await authFetch(`/api/addresses/${id}/set-default`, {
      method: 'PATCH',
    });

    if (!response.ok) {
      throw new Error('Failed to set default address');
    }

    return response.json();
  },
};

export { API_BASE_URL };

export default {
  products: productsApi,
  categories: categoriesApi,
  orders: ordersApi,
  addresses: addressesApi,
  wishlist: wishlistApi,
};